import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button, Card, CardContent } from "@opencloud/ui";
import { automaticFailureView } from "../automatic-failures.js";
import type { AgentAutomaticFailure, AiAutomaticStatus } from "../types.js";

/** What failure cards in one app chat share. */
export interface AutomaticFailureContext {
  appName: string;
  /** The app's automatic choice now; null until it is read or where it can't be. */
  status: AiAutomaticStatus | null;
  /** The dashboard's App → AI page. */
  appAiUrl: string | null;
  /** Opens the composer's model picker; absent for people who can't pick. */
  onPickModel?: (() => void) | undefined;
}

/**
 * An automatic repair, scheduled task or AI calls from the app's code that
 * couldn't run on the app's chosen model. Nothing fell back to another
 * provider; once the app's automatic choice can run, the card says so.
 */
export function AutomaticFailureCard({
  failure,
  messageId,
  focused,
  readOnly,
  context,
  formatTime,
}: {
  failure: AgentAutomaticFailure;
  messageId: string;
  focused: boolean;
  readOnly: boolean;
  context: AutomaticFailureContext | undefined;
  formatTime: (value: string) => string;
}) {
  const view = automaticFailureView(failure, {
    appName: context?.appName ?? "this app",
    status: context?.status ?? null,
    formatTime,
  });
  const Icon = view.resolved ? CheckCircle2 : AlertTriangle;
  const actions = !readOnly && !view.resolved && (context?.onPickModel || context?.appAiUrl);
  return (
    <Card
      id={`message-${messageId}`}
      data-message-id={messageId}
      data-search-focus={focused || undefined}
      data-automatic-failure={failure.kind}
      data-resolved={view.resolved}
      className="a3-automatic-failure"
      role="note"
      aria-label={view.title}
    >
      <CardContent>
        <div className="a3-automatic-failure-head">
          <Icon aria-hidden="true" />
          <strong>{view.title}</strong>
          {view.time.label ? <time dateTime={view.time.dateTime}>{view.time.label}</time> : null}
        </div>
        {view.subject ? <p className="a3-automatic-failure-subject">{view.subject}</p> : null}
        <p className="a3-automatic-failure-status">{view.status}</p>
        {actions ? (
          <div className="a3-automatic-failure-actions">
            {context?.onPickModel ? (
              <Button type="button" size="sm" variant="outline" onClick={context.onPickModel}>
                Pick another model
              </Button>
            ) : null}
            {context?.appAiUrl ? (
              <Button size="sm" variant="ghost" nativeButton={false} render={<a href={context.appAiUrl} />}>
                Open App → AI
              </Button>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
